import { lazy, Suspense } from 'react';
import {
  HeadContent,
  Outlet,
  createRootRoute,
  useSearch,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { PageWrap } from '../components/Page/Page';
import { useWorkflowState, WorkflowContext } from '../workflow/hooks';
import { validateSearch } from '../utils/searchParams';
import { FullscreenLoader } from '../components/FullscreenLoader/FullscreenLoader';
import { getRetailerSettingsQueryFn } from '../graphql/query/getRetailerSettings';
import { rawString } from '../components/Language/Language';
import { FullscreenError } from '../components/FullscreenError/FullscreenError';
import { QUERIES } from '../utils/constants';
import { PermissionsProvider } from '../providers/permissions/PermissionsProvider';
import type { UserType } from '../providers/permissions/types';

const TanStackRouterDevtools =
  import.meta.env.VITE_VITE_DEVTOOLS_ENABLED === 'true' && !import.meta.env.PROD
    ? lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      )
    : () => null;

const TanStackQueryDevtools =
  import.meta.env.VITE_VITE_DEVTOOLS_ENABLED === 'true' && !import.meta.env.PROD
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((res) => ({
          default: res.ReactQueryDevtools,
        })),
      )
    : () => null;

/**
 * Determine user type from query parameters.
 * Currently supports `?agent=true` for agent mode simulation.
 * In production, agent status will be determined by Salesforce authentication.
 */
const useUserType = (): UserType => {
  const search = useSearch({ strict: false });
  // agent is coerced to boolean by the search schema
  const isAgent = search?.agent === true;
  return isAgent ? 'agent' : 'customer';
};

const Root = () => {
  const workflowState = useWorkflowState();
  const { currentState } = workflowState;
  const userType = useUserType();

  const {
    isLoading: isRetailerSettingsLoading,
    isError: isRetailerSettingsError,
  } = useQuery({
    queryKey: [QUERIES.GET_RETAILER_SETTINGS],
    queryFn: getRetailerSettingsQueryFn,
    retry: false,
  });

  const loadingStates = ['loadConfig', 'loadOrder', 'loadCart'];
  const currentStateString =
    typeof currentState === 'string' ? currentState : '';

  if (isRetailerSettingsLoading || loadingStates.includes(currentStateString)) {
    return <FullscreenLoader />;
  }
  if (isRetailerSettingsError || currentStateString === 'error') {
    return <FullscreenLoader error={true} />;
  }

  return (
    <PermissionsProvider userType={userType}>
      <WorkflowContext.Provider value={workflowState}>
        <HeadContent />
        <Outlet />
      </WorkflowContext.Provider>
      <Suspense>
        <TanStackRouterDevtools position="bottom-right" />
        <TanStackQueryDevtools buttonPosition="bottom-left" />
      </Suspense>
    </PermissionsProvider>
  );
};

export const Route = createRootRoute({
  validateSearch,
  component: Root,
  head: () => ({
    meta: [
      {
        title:
          rawString({ langKey: 'pdx.global.page_title' }) ?? 'Easy Returns',
      },
    ],
  }),
  notFoundComponent: () => (
    <PageWrap>
      <FullscreenError />
    </PageWrap>
  ),
});
